import { useEffect, useMemo, useState } from 'react';
import type { GeneralInfoPicklistsProp } from '../api/generalInfoApi';
import { toPrettyJson } from '../api/httpErrorFormat';
import { isOpeningTimesWeekdayKey } from '../utils/openingTimesWeekdays';
import {
  applyOpeningTimesDayOverride,
  applyOpeningTimesGroupSchedule,
  deriveOpeningTimesGroupSchedule,
  expandOpeningTimesWeekdaysToIndividual,
  openingTimesEffectiveOverrides,
  openingTimesGetDayValue,
  openingTimesGroupDaysForDay,
  openingTimesNormalizeDayValue,
  openingTimesScheduleGroupLabelKey,
  openingTimesScheduleGroupsForLocale,
  openingTimesWeekdaysAvailableToCustomize,
  removeOpeningTimesDayOverride,
  resolveOpeningTimesStoredWeekdayKey,
  type OpeningTimesScheduleGroupKey,
} from '../utils/openingTimesLocale';
import {
  eachInclusiveIsoDates,
  endDateFromStartAndDays,
  groupConsecutiveIsoDatesIntoRanges,
  inclusiveDayCount,
  removeBlackoutRangeFromOpeningTimes,
  replaceBlackoutRangeInOpeningTimes,
  type IsoDateRange,
} from '../utils/blackoutDateRange';
import { formatCalendarDayKeyLabel } from '../utils/assetBookingCalendar';
import {
  localizeAssetFieldLabel,
  localizeAssetTypeLabel,
  localizeGeoLabel,
  localizeWeekdayLabel,
  sortRecordKeysForDisplay,
  useI18n,
  type MessageKey,
} from '../i18n';
import type { AppLanguage } from '../i18n/types';
import type { TranslateFn } from '../i18n/operationLabels';
import { LocaleTimeInput } from './LocaleTimeInput';
import { PasswordInputRow } from './PasswordInputRow';
import { IsraelSettlementCitySelect } from './IsraelSettlementCitySelect';
import { BirthDateSplitPicker } from './BirthDateSplitPicker';
import { IsraelAddressFieldset } from './IsraelAddressFieldset';
import directionStyles from './formInputDirection.module.css';
import { formInputClassName, formInputDir } from './formInputDirection';
import { daysInMonth, parseIsoDateParts, toIsoDate } from '../utils/birthDateIso';
import { isPhoneWireFieldKey } from '../utils/phoneE164';
import { isAddressCityField } from '../utils/israelSettlements';
import { isAddressFieldGroup } from '../i18n/assetFieldLabels';
import styles from './DynamicRecordForm.module.css';

function keyLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function MandatoryAsterisk({ visible }: { visible: boolean }) {
  if (!visible) {
    return null;
  }
  return (
    <span className={styles.mandatoryAsterisk} aria-hidden title="Required">
      *
    </span>
  );
}

function showMandatoryMarker(
  path: readonly string[],
  fieldKey: string,
  markerKeys?: readonly string[],
): boolean {
  if (!markerKeys?.length) {
    return false;
  }
  const dotted = [...path, fieldKey].join('.');
  if (markerKeys.includes(dotted)) {
    return true;
  }
  return path.length === 0 && markerKeys.includes(fieldKey);
}

function localTodayYmd(): { y: number; m: number; d: number } {
  const n = new Date();
  return { y: n.getFullYear(), m: n.getMonth() + 1, d: n.getDate() };
}

/** Calendar order: negative if a < b, zero if equal, positive if a > b. */
function compareYmd(a: { y: number; m: number; d: number }, b: { y: number; m: number; d: number }): number {
  if (a.y !== b.y) {
    return a.y - b.y;
  }
  if (a.m !== b.m) {
    return a.m - b.m;
  }
  return a.d - b.d;
}

function intlMonthLabel(month1to12: number, language: AppLanguage, short: boolean): string {
  const d = new Date(2000, month1to12 - 1, 1);
  return new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : 'en-US', {
    month: short ? 'short' : 'long',
  }).format(d);
}

/** If `key` is `YYYY-MM-DD`, show a localized calendar label; else weekday name. */
function openingTimesDayDisplayLabel(dayKey: string, language: AppLanguage, t: TranslateFn): string {
  const p = parseIsoDateParts(dayKey);
  if (p) {
    const d = new Date(p.y, p.m - 1, p.d);
    return new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(d);
  }
  if (isOpeningTimesWeekdayKey(dayKey)) {
    return localizeWeekdayLabel(dayKey, t);
  }
  return keyLabel(dayKey);
}

function canonicalGenderSelectValue(raw: string): '' | 'male' | 'female' | 'other' {
  const t = raw.trim().toLowerCase();
  if (t === 'male' || t === 'female' || t === 'other') {
    return t;
  }
  return '';
}

type OpeningTimesIsoDateTripleSelectProps = {
  idPrefix: string;
  value: string;
  onChange: (nextIso: string) => void;
};

/**
 * Day / month / year selects (same layout as birth-date / booking-style picks) → `YYYY-MM-DD` keys under `opening_times`.
 * Only today and future calendar days (local timezone).
 */
function OpeningTimesIsoDateTripleSelect({ idPrefix, value, onChange }: OpeningTimesIsoDateTripleSelectProps) {
  const { t, language } = useI18n();
  const [y, setY] = useState('');
  const [m, setM] = useState('');
  const [d, setD] = useState('');

  useEffect(() => {
    const p = parseIsoDateParts(value);
    const today = localTodayYmd();
    if (p && compareYmd(p, today) >= 0) {
      setY(String(p.y));
      setM(String(p.m));
      setD(String(p.d));
    } else {
      setY('');
      setM('');
      setD('');
    }
  }, [value]);

  const today = localTodayYmd();
  const minYear = today.y;
  const maxYear = today.y + 10;

  const yi = y ? Number(y) : NaN;
  const mi = m ? Number(m) : NaN;
  const yearOk = Number.isFinite(yi) && yi >= minYear && yi <= maxYear;
  const monthOk = Number.isFinite(mi) && mi >= 1 && mi <= 12;
  const maxDay = yearOk && monthOk ? daysInMonth(yi, mi) : 31;
  const monthLow = yearOk && yi === today.y ? today.m : 1;
  const monthHigh = 12;
  const dayLow =
    yearOk && monthOk && yi === today.y && mi === today.m ? Math.min(today.d, maxDay) : 1;
  const dayHigh = yearOk && monthOk ? maxDay : 31;

  const years = useMemo(() => {
    const list: number[] = [];
    for (let yr = minYear; yr <= maxYear; yr += 1) {
      list.push(yr);
    }
    return list;
  }, [minYear, maxYear]);

  const monthOptions = useMemo(() => {
    const out: { value: number; label: string }[] = [];
    for (let mo = monthLow; mo <= monthHigh; mo += 1) {
      out.push({ value: mo, label: intlMonthLabel(mo, language, false) });
    }
    return out;
  }, [language, monthLow, monthHigh]);

  const days = useMemo(() => {
    const n = Math.max(0, dayHigh - dayLow + 1);
    return Array.from({ length: n }, (_, i) => dayLow + i);
  }, [dayLow, dayHigh]);

  const commit = (ny: string, nm: string, nd: string) => {
    setY(ny);
    setM(nm);
    setD(nd);
    if (ny && nm && nd) {
      let yi2 = Number(ny);
      let mi2 = Number(nm);
      let di2 = Number(nd);
      const cap = daysInMonth(yi2, mi2);
      if (di2 > cap) {
        di2 = cap;
      }
      const todayYmd = localTodayYmd();
      if (compareYmd({ y: yi2, m: mi2, d: di2 }, todayYmd) < 0) {
        yi2 = todayYmd.y;
        mi2 = todayYmd.m;
        di2 = todayYmd.d;
      }
      setY(String(yi2));
      setM(String(mi2));
      setD(String(di2));
      onChange(toIsoDate(yi2, mi2, di2));
    } else {
      onChange('');
    }
  };

  return (
    <div
      className={styles.birthDateSplitRow}
      role="group"
      aria-label={t('form.openingDate')}
    >
      <div className={styles.birthDatePart}>
        <label className={styles.birthDatePartLabel} htmlFor={`${idPrefix}-day`}>
          {t('common.day')}
        </label>
        <select
          id={`${idPrefix}-day`}
          className={styles.selectInput}
          value={d}
          onChange={(event) => commit(y, m, event.target.value)}
        >
          <option value="">{t('common.day')}</option>
          {days.map((dayNum) => (
            <option key={dayNum} value={String(dayNum)}>
              {dayNum}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.birthDatePart}>
        <label className={styles.birthDatePartLabel} htmlFor={`${idPrefix}-month`}>
          {t('common.month')}
        </label>
        <select
          id={`${idPrefix}-month`}
          className={styles.selectInput}
          value={m}
          onChange={(event) => {
            const nm = event.target.value;
            let nd = d;
            if (nm && y && d) {
              const yiN = Number(y);
              const miN = Number(nm);
              const diN = Number(d);
              const cap = daysInMonth(yiN, miN);
              const todayYmd = localTodayYmd();
              let minD = 1;
              if (yiN === todayYmd.y && miN === todayYmd.m) {
                minD = todayYmd.d;
              }
              if (diN > cap) {
                nd = String(cap);
              } else if (diN < minD) {
                nd = String(minD);
              }
            }
            commit(y, nm, nd);
          }}
        >
          <option value="">{t('common.month')}</option>
          {monthOptions.map(({ value: mv, label }) => (
            <option key={mv} value={String(mv)}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.birthDatePart}>
        <label className={styles.birthDatePartLabel} htmlFor={`${idPrefix}-year`}>
          {t('common.year')}
        </label>
        <select
          id={`${idPrefix}-year`}
          className={styles.selectInput}
          value={y}
          onChange={(event) => {
            const ny = event.target.value;
            const todayYmd = localTodayYmd();
            let nm = m;
            let nd2 = d;
            if (ny && Number(ny) === todayYmd.y) {
              if (!m || Number(m) < todayYmd.m) {
                nm = String(todayYmd.m);
              }
            }
            if (ny && nm && nd2) {
              const yiN = Number(ny);
              const miN = Number(nm);
              const diN = Number(nd2);
              const cap = daysInMonth(yiN, miN);
              let minD = 1;
              if (yiN === todayYmd.y && miN === todayYmd.m) {
                minD = todayYmd.d;
              }
              if (diN > cap) {
                nd2 = String(cap);
              } else if (diN < minD) {
                nd2 = String(minD);
              }
            }
            commit(ny, nm, nd2);
          }}
        >
          <option value="">{t('common.year')}</option>
          {years.map((yr) => (
            <option key={yr} value={String(yr)}>
              {yr}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

type PayloadFormFieldsProps = {
  data: Record<string, unknown>;
  path: string[];
  onFieldChange: (path: string[], value: unknown) => void;
  idPrefix: string;
  /** When set (e.g. register flow), append policy text to the primary `password` field label only. */
  showPasswordRequirements?: boolean;
  /** When set (e.g. register / create asset), root `country_id` / `state_id` use `/general-info/*`; Create Asset `asset_type_id` uses `GET /assets/asset-types`. */
  generalInfoPicklists?: GeneralInfoPicklistsProp;
  /** When set (e.g. register), root payload keys listed here render a red required asterisk on the label. */
  mandatoryMarkerKeys?: readonly string[];
  /** Optional root-level label overrides (e.g. contact first/last name on asset-owner register). */
  rootFieldLabelKeys?: Partial<Record<string, MessageKey>>;
  /** When true, root `birth_date` uses day / month / year dropdowns (still stored as `YYYY-MM-DD`). */
  birthDateDayMonthYear?: boolean;
  /** When true, root `gender` uses Male / Female / Other (stored as lowercase `male`|`female`|`other`). */
  registerGenderMaleFemaleOther?: boolean;
  /** When set, matched paths render disabled (e.g. asset PATCH forbid/ignored roots). */
  readonlyPathPredicate?: (pathSegments: readonly string[]) => boolean;
  theme?: 'dark' | 'light';
  /** Two-column grid on asset create/update to reduce vertical scroll. */
  compactLayout?: boolean;
  /** When false, hide specific-date and blackout date overrides. */
  openingTimesAllowSpecificDates?: boolean;
};

function normalizeWireKey(key: string): string {
  return key.trim().toLowerCase().replace(/\s+/g, '_');
}

function fieldRowClassName(compactLayout: boolean, path: string[], key: string): string {
  if (!compactLayout) {
    return styles.fieldRow;
  }
  if (path.length === 0) {
    const normalized = normalizeWireKey(key);
    if (normalized === 'asset_name' || normalized === 'name' || normalized === 'description' || normalized === 'sub_title') {
      return `${styles.fieldRow} ${styles.fieldRowFull}`;
    }
  }
  return styles.fieldRow;
}

function fieldsContainerClassName(compactLayout: boolean, path: string[]): string {
  if (path.length > 0) {
    return compactLayout ? styles.nestedFieldsCompact : styles.nestedFields;
  }
  return compactLayout ? styles.rootFieldsCompact : styles.rootFields;
}

function fieldsetClassName(compactLayout: boolean, key: string): string {
  const addressFieldset = normalizeWireKey(key) === 'address';
  if (compactLayout && addressFieldset) {
    return `${styles.fieldset} ${styles.fieldRowFull}`;
  }
  return styles.fieldset;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isNumberArray(value: unknown[]): value is number[] {
  return value.length === 0 || value.every((x) => typeof x === 'number' && !Number.isNaN(x as number));
}

function isStringArray(value: unknown[]): value is string[] {
  return value.length > 0 && value.every((x) => typeof x === 'string');
}

/** General-info IDs may deserialize as `"42"`; normalize only for dropdown matching. */
function coerceRootNumericId(raw: unknown): number | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (t === '') {
      return null;
    }
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function isOpeningTimesDayPath(path: string[]): boolean {
  return path.length >= 2 && path[path.length - 2] === 'opening_times';
}

/** Day entry is `{ open, close }` with only those two string fields (e.g. Mon–Fri / exceptions). */
function isOpenClosePair(v: unknown): v is { open: string; close: string } {
  if (!isPlainObject(v)) {
    return false;
  }
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o);
  if (keys.length !== 2 || !keys.includes('open') || !keys.includes('close')) {
    return false;
  }
  return typeof o.open === 'string' && typeof o.close === 'string';
}

/** Some API rows map a closed weekday as `{ closed: true }` (no `{ open, close }`). */
function isExclusiveClosedMarkerObject(value: unknown): boolean {
  if (!isPlainObject(value)) {
    return false;
  }
  const o = value as Record<string, unknown>;
  if ('open' in o || 'close' in o) {
    return false;
  }
  if (!('closed' in o)) {
    return false;
  }
  const c = o.closed;
  return !(c === false || c === 'false' || c === 0);
}

/** UI + API may use scalar `closed`, empty pair, or empty strings inside `{ open, close }`. */
function isVisuallyClosedOpeningDay(value: unknown): boolean {
  if (value === 'closed' || value === '') {
    return true;
  }
  if (isExclusiveClosedMarkerObject(value)) {
    return true;
  }
  if (isOpenClosePair(value)) {
    return value.open.trim() === '' && value.close.trim() === '';
  }
  return false;
}

function isOpeningTimeDayValue(value: unknown): boolean {
  if (value === 'closed' || value === '') {
    return true;
  }
  if (isExclusiveClosedMarkerObject(value)) {
    return true;
  }
  if (isOpenClosePair(value)) {
    return true;
  }
  return false;
}

const DEFAULT_DAY_HOURS: { open: string; close: string } = { open: '09:00', close: '19:00' };

/** Full calendar day for APIs that accept a simple open/close range (midnight→end-of-day). */
const OPEN_ALL_DAY_HOURS: { open: string; close: string } = { open: '00:00', close: '23:59' };

function pairIsOpenAllDay(pair: { open: string; close: string }): boolean {
  return pair.open.trim() === '00:00' && pair.close.trim() === '23:59';
}

/** `input type="time"` only accepts `HH:MM` in most browsers; avoid console/DOM errors from `HH:MM:SS` or bad strings. */
function timeStringForHtmlInput(raw: string): string {
  if (raw == null) {
    return '';
  }
  const t = String(raw).trim();
  if (t === '') {
    return '';
  }
  if (/^\d{2}:\d{2}$/.test(t)) {
    return t;
  }
  if (/^\d{2}:\d{2}:\d{2}/.test(t)) {
    return t.slice(0, 5);
  }
  const m = t.match(/^(\d{1,2}):(\d{1,2})$/);
  if (m) {
    const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
    const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  return '';
}

type OpeningTimeDayFieldProps = {
  id: string;
  dayKey: string;
  value: unknown;
  fieldPath: string[];
  onFieldChange: (path: string[], v: unknown) => void;
  readOnly?: boolean;
  displayLabel?: string;
};

function OpeningTimeDayField({
  id,
  dayKey,
  value,
  fieldPath,
  onFieldChange,
  readOnly,
  displayLabel,
}: OpeningTimeDayFieldProps) {
  const { t, language } = useI18n();
  const closed = isVisuallyClosedOpeningDay(value);
  const pairRaw = isOpenClosePair(value) && !isVisuallyClosedOpeningDay(value)
    ? { open: value.open, close: value.close }
    : { ...DEFAULT_DAY_HOURS };
  const pair = {
    open: timeStringForHtmlInput(pairRaw.open),
    close: timeStringForHtmlInput(pairRaw.close),
  };
  const dayLabel = displayLabel ?? openingTimesDayDisplayLabel(dayKey, language, t);

  if (closed) {
    return (
      <div className={styles.fieldRow}>
        <span className={styles.inputLabel}>{dayLabel}</span>
        <div className={styles.openingTimeDayRow}>
          <span className={styles.dayClosedPill} aria-label={t('assetDetail.closed')}>
            {t('assetDetail.closed')}
          </span>
          <button
            type="button"
            className={styles.toggleHoursButton}
            disabled={readOnly}
            onClick={() => onFieldChange(fieldPath, { ...DEFAULT_DAY_HOURS })}
          >
            {t('openingTimes.setOpenHours')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.fieldRow}>
      <span className={styles.inputLabel}>{dayLabel}</span>
      <div className={styles.openingTimeDayRow}>
        <LocaleTimeInput
          id={`${id}-open`}
          className={styles.textInput}
          selectClassName={styles.selectInput}
          language={language}
          ariaLabel={t('openingTimes.open')}
          value={pair.open}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={(open) => onFieldChange(fieldPath, { open, close: pair.close })}
        />
        <span className={styles.openingTimeDash} aria-hidden="true">
          –
        </span>
        <LocaleTimeInput
          id={`${id}-close`}
          className={styles.textInput}
          selectClassName={styles.selectInput}
          language={language}
          ariaLabel={t('openingTimes.close')}
          value={pair.close}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={(close) => onFieldChange(fieldPath, { open: pair.open, close })}
        />
        <button
          type="button"
          className={styles.toggleHoursButton}
          disabled={readOnly}
          onClick={() => onFieldChange(fieldPath, { ...OPEN_ALL_DAY_HOURS })}
        >
          {t('openingTimes.openAllDay')}
        </button>
        <button
          type="button"
          className={styles.toggleHoursButton}
          disabled={readOnly}
          onClick={() => onFieldChange(fieldPath, 'closed')}
        >
          {t('openingTimes.setClosed')}
        </button>
      </div>
      {pairIsOpenAllDay(pair) ? (
        <p className={styles.openingTimesAllDayHint}>
          {t('openingTimes.allDayHint', { open: OPEN_ALL_DAY_HOURS.open, close: OPEN_ALL_DAY_HOURS.close })}
        </p>
      ) : null}
    </div>
  );
}

type BlackoutRangeRowProps = {
  range: IsoDateRange;
  idPrefix: string;
  readOnly: boolean;
  language: AppLanguage;
  onSave: (oldRange: IsoDateRange, nextStartIso: string, nextEndIso: string) => void;
  onRemove: (range: IsoDateRange) => void;
};

function BlackoutRangeRow({
  range,
  idPrefix,
  readOnly,
  language,
  onSave,
  onRemove,
}: BlackoutRangeRowProps) {
  const { t } = useI18n();
  const [startIso, setStartIso] = useState(range.startIso);
  const [endIso, setEndIso] = useState(range.endIso);
  const [daysText, setDaysText] = useState(String(range.dayCount));
  const [error, setError] = useState<string | null>(null);
  const rangeKey = `${range.startIso}__${range.endIso}`;

  useEffect(() => {
    setStartIso(range.startIso);
    setEndIso(range.endIso);
    setDaysText(String(range.dayCount));
    setError(null);
  }, [range.startIso, range.endIso, range.dayCount]);

  function syncFromStart(nextStartIso: string) {
    setStartIso(nextStartIso);
    setError(null);
    const start = parseIsoDateParts(nextStartIso);
    if (!start) {
      return;
    }
    const daysNum = Number(daysText);
    if (Number.isFinite(daysNum) && daysNum >= 1) {
      const end = endDateFromStartAndDays(start, daysNum);
      setEndIso(toIsoDate(end.y, end.m, end.d));
      return;
    }
    const end = parseIsoDateParts(endIso);
    if (end && compareYmd(end, start) >= 0) {
      setDaysText(String(inclusiveDayCount(start, end)));
    } else {
      setDaysText('1');
      setEndIso(nextStartIso);
    }
  }

  function syncFromDays(nextDaysText: string) {
    setDaysText(nextDaysText);
    setError(null);
    const start = parseIsoDateParts(startIso);
    const daysNum = Number(nextDaysText);
    if (!start || !Number.isFinite(daysNum) || daysNum < 1) {
      return;
    }
    const end = endDateFromStartAndDays(start, daysNum);
    setEndIso(toIsoDate(end.y, end.m, end.d));
  }

  function syncFromEnd(nextEndIso: string) {
    setEndIso(nextEndIso);
    setError(null);
    const start = parseIsoDateParts(startIso);
    const end = parseIsoDateParts(nextEndIso);
    if (!start || !end) {
      return;
    }
    if (compareYmd(end, start) < 0) {
      setError(t('openingTimes.blackoutInvalidRange'));
      return;
    }
    setDaysText(String(inclusiveDayCount(start, end)));
  }

  function saveRange() {
    const start = parseIsoDateParts(startIso);
    const end = parseIsoDateParts(endIso);
    const today = localTodayYmd();
    if (!start || !end) {
      setError(t('openingTimes.pickDateFirst'));
      return;
    }
    if (compareYmd(start, today) < 0 || compareYmd(end, today) < 0) {
      setError(t('openingTimes.dateMustBeFuture'));
      return;
    }
    if (compareYmd(end, start) < 0) {
      setError(t('openingTimes.blackoutInvalidRange'));
      return;
    }
    if (startIso === range.startIso && endIso === range.endIso) {
      return;
    }
    setError(null);
    onSave(range, startIso, endIso);
  }

  const summaryLabel = `${formatCalendarDayKeyLabel(range.startIso, language)} – ${formatCalendarDayKeyLabel(range.endIso, language)} (${t('openingTimes.blackoutRangeDays', { n: range.dayCount })})`;

  return (
    <details className={styles.blackoutRangeItem}>
      <summary className={styles.blackoutRangeSummary}>{summaryLabel}</summary>
      <div className={styles.blackoutRangeBody}>
        <div className={styles.openingTimesBlackoutFields}>
          <div className={styles.openingTimesBlackoutField}>
            <span className={styles.birthDatePartLabel}>{t('openingTimes.blackoutStart')}</span>
            <OpeningTimesIsoDateTripleSelect
              idPrefix={`${idPrefix}-blackout-range-${rangeKey}-start`}
              value={startIso}
              onChange={syncFromStart}
            />
          </div>
          <div className={styles.openingTimesBlackoutField}>
            <label
              className={styles.birthDatePartLabel}
              htmlFor={`${idPrefix}-blackout-range-${rangeKey}-days`}
            >
              {t('openingTimes.blackoutDays')}
            </label>
            <input
              id={`${idPrefix}-blackout-range-${rangeKey}-days`}
              className={styles.textInput}
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={daysText}
              disabled={readOnly}
              onChange={(event) => syncFromDays(event.target.value)}
            />
          </div>
          <div className={styles.openingTimesBlackoutField}>
            <span className={styles.birthDatePartLabel}>{t('openingTimes.blackoutEnd')}</span>
            <OpeningTimesIsoDateTripleSelect
              idPrefix={`${idPrefix}-blackout-range-${rangeKey}-end`}
              value={endIso}
              onChange={syncFromEnd}
            />
          </div>
        </div>
        <div className={styles.openingTimesAddDateRow}>
          <button
            type="button"
            className={styles.toggleHoursButton}
            disabled={readOnly}
            onClick={saveRange}
          >
            {t('openingTimes.saveBlackoutRange')}
          </button>
          <button
            type="button"
            className={styles.openingTimesRemoveSpecialButton}
            disabled={readOnly}
            onClick={() => onRemove(range)}
          >
            {t('openingTimes.removeBlackoutRange')}
          </button>
        </div>
        {error ? (
          <p className={styles.picklistError} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </details>
  );
}

type OpeningTimesFieldsetProps = {
  data: Record<string, unknown>;
  path: string[];
  idPrefix: string;
  onFieldChange: (path: string[], value: unknown) => void;
  readonlyPathPredicate?: (path: readonly string[]) => boolean;
  allowSpecificDates?: boolean;
};

function OpeningTimesFieldset({
  data,
  path,
  idPrefix,
  onFieldChange,
  readonlyPathPredicate,
  allowSpecificDates = true,
}: OpeningTimesFieldsetProps) {
  const { t, language } = useI18n();
  const [specificPickedIso, setSpecificPickedIso] = useState('');
  const [specificSelectedKey, setSpecificSelectedKey] = useState('');
  const [specificDateError, setSpecificDateError] = useState<string | null>(null);
  const [blackoutStartIso, setBlackoutStartIso] = useState('');
  const [blackoutEndIso, setBlackoutEndIso] = useState('');
  const [blackoutDaysText, setBlackoutDaysText] = useState('1');
  const [blackoutError, setBlackoutError] = useState<string | null>(null);
  const [pendingOverrideDays, setPendingOverrideDays] = useState<Set<string>>(() => new Set());
  const openingTimesFrozen = readonlyPathPredicate?.(path) ?? false;

  const scheduleGroups = openingTimesScheduleGroupsForLocale(language);
  const groupSchedules = useMemo(() => {
    const schedules = new Map<OpeningTimesScheduleGroupKey, unknown>();
    for (const group of scheduleGroups) {
      schedules.set(group.key, deriveOpeningTimesGroupSchedule(data, group.days));
    }
    return schedules;
  }, [data, scheduleGroups]);
  const effectiveOverrides = useMemo(
    () => openingTimesEffectiveOverrides(data, language, pendingOverrideDays),
    [data, language, pendingOverrideDays],
  );
  const overrideSkipSet = useMemo(() => new Set(effectiveOverrides), [effectiveOverrides]);
  const customizableWeekdays = useMemo(
    () => openingTimesWeekdaysAvailableToCustomize(language, effectiveOverrides),
    [language, effectiveOverrides],
  );

  const { specificDateKeys, blackoutDateKeys } = useMemo(() => {
    const specific: string[] = [];
    const blackout: string[] = [];
    for (const key of Object.keys(data)) {
      if (isOpeningTimesWeekdayKey(key)) {
        continue;
      }
      if (parseIsoDateParts(key) && openingTimesNormalizeDayValue(data[key]) === 'closed') {
        blackout.push(key);
      } else {
        specific.push(key);
      }
    }
    specific.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    blackout.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return { specificDateKeys: specific, blackoutDateKeys: blackout };
  }, [data]);

  const blackoutRanges = useMemo(
    () => groupConsecutiveIsoDatesIntoRanges(blackoutDateKeys),
    [blackoutDateKeys],
  );

  useEffect(() => {
    if (specificSelectedKey && !specificDateKeys.includes(specificSelectedKey)) {
      setSpecificSelectedKey(specificDateKeys[0] ?? '');
    }
  }, [specificDateKeys, specificSelectedKey]);

  function syncBlackoutFromStart(nextStartIso: string) {
    setBlackoutStartIso(nextStartIso);
    setBlackoutError(null);
    const start = parseIsoDateParts(nextStartIso);
    if (!start) {
      return;
    }
    const daysNum = Number(blackoutDaysText);
    if (Number.isFinite(daysNum) && daysNum >= 1) {
      const end = endDateFromStartAndDays(start, daysNum);
      setBlackoutEndIso(toIsoDate(end.y, end.m, end.d));
      return;
    }
    const end = parseIsoDateParts(blackoutEndIso);
    if (end) {
      if (compareYmd(end, start) < 0) {
        setBlackoutEndIso(nextStartIso);
        setBlackoutDaysText('1');
      } else {
        setBlackoutDaysText(String(inclusiveDayCount(start, end)));
      }
    } else {
      setBlackoutDaysText('1');
      setBlackoutEndIso(nextStartIso);
    }
  }

  function syncBlackoutFromDays(nextDaysText: string) {
    setBlackoutDaysText(nextDaysText);
    setBlackoutError(null);
    const start = parseIsoDateParts(blackoutStartIso);
    const daysNum = Number(nextDaysText);
    if (!start || !Number.isFinite(daysNum) || daysNum < 1) {
      return;
    }
    const end = endDateFromStartAndDays(start, daysNum);
    setBlackoutEndIso(toIsoDate(end.y, end.m, end.d));
  }

  function syncBlackoutFromEnd(nextEndIso: string) {
    setBlackoutEndIso(nextEndIso);
    setBlackoutError(null);
    const start = parseIsoDateParts(blackoutStartIso);
    const end = parseIsoDateParts(nextEndIso);
    if (!start || !end) {
      return;
    }
    if (compareYmd(end, start) < 0) {
      setBlackoutError(t('openingTimes.blackoutInvalidRange'));
      return;
    }
    setBlackoutDaysText(String(inclusiveDayCount(start, end)));
  }

  function addSpecificDate(dateKeyRaw: string) {
    if (openingTimesFrozen) {
      return;
    }
    const key = dateKeyRaw.trim();
    if (key === '') {
      setSpecificDateError(t('openingTimes.pickDateThenAdd'));
      return;
    }
    const parts = parseIsoDateParts(key);
    const today = localTodayYmd();
    if (!parts || compareYmd(parts, today) < 0) {
      setSpecificDateError(t('openingTimes.dateMustBeFuture'));
      return;
    }
    if (key in data) {
      setSpecificDateError(t('openingTimes.dateAlreadyExists', { date: key }));
      return;
    }
    setSpecificDateError(null);
    onFieldChange([...path, key], { ...DEFAULT_DAY_HOURS });
    setSpecificSelectedKey(key);
  }

  function addBlackoutDates() {
    if (openingTimesFrozen) {
      return;
    }
    const start = parseIsoDateParts(blackoutStartIso);
    const end = parseIsoDateParts(blackoutEndIso);
    const today = localTodayYmd();
    if (!start) {
      setBlackoutError(t('openingTimes.blackoutPickStart'));
      return;
    }
    if (!end) {
      setBlackoutError(t('openingTimes.pickDateFirst'));
      return;
    }
    if (compareYmd(start, today) < 0 || compareYmd(end, today) < 0) {
      setBlackoutError(t('openingTimes.dateMustBeFuture'));
      return;
    }
    if (compareYmd(end, start) < 0) {
      setBlackoutError(t('openingTimes.blackoutInvalidRange'));
      return;
    }
    const dates = eachInclusiveIsoDates(blackoutStartIso, blackoutEndIso);
    if (dates.length === 0) {
      setBlackoutError(t('openingTimes.blackoutInvalidRange'));
      return;
    }
    const next = { ...data };
    for (const iso of dates) {
      next[iso] = 'closed';
    }
    setBlackoutError(null);
    onFieldChange(path, next);
    setBlackoutStartIso('');
    setBlackoutEndIso('');
    setBlackoutDaysText('1');
  }

  function removeBlackoutRange(range: IsoDateRange) {
    if (openingTimesFrozen) {
      return;
    }
    onFieldChange(path, removeBlackoutRangeFromOpeningTimes(data, range.startIso, range.endIso));
  }

  function saveBlackoutRange(oldRange: IsoDateRange, newStartIso: string, newEndIso: string) {
    if (openingTimesFrozen) {
      return;
    }
    onFieldChange(
      path,
      replaceBlackoutRangeInOpeningTimes(
        data,
        oldRange.startIso,
        oldRange.endIso,
        newStartIso,
        newEndIso,
      ),
    );
  }

  function removeSpecialDateKey(specialKey: string) {
    const next = { ...data };
    delete next[specialKey];
    onFieldChange(path, next);
  }

  function setGroupSchedule(groupDays: readonly string[], nextValue: unknown) {
    if (openingTimesFrozen) {
      return;
    }
    onFieldChange(path, applyOpeningTimesGroupSchedule(data, groupDays, overrideSkipSet, nextValue));
  }

  function expandAllWeekdays() {
    if (openingTimesFrozen || customizableWeekdays.length === 0) {
      return;
    }
    const { nextData, expandedDays } = expandOpeningTimesWeekdaysToIndividual(data, language);
    setPendingOverrideDays((prev) => new Set(Array.from(prev).concat(expandedDays)));
    onFieldChange(path, nextData);
  }

  function addDayOverride(canonical: string) {
    if (openingTimesFrozen || !canonical) {
      return;
    }
    const groupDays = openingTimesGroupDaysForDay(canonical, language);
    const groupSchedule = deriveOpeningTimesGroupSchedule(data, groupDays);
    setPendingOverrideDays((prev) => new Set(Array.from(prev).concat(canonical)));
    onFieldChange(path, applyOpeningTimesDayOverride(data, canonical, groupSchedule));
  }

  function removeDayOverride(canonical: string) {
    if (openingTimesFrozen) {
      return;
    }
    setPendingOverrideDays((prev) => {
      const next = new Set(prev);
      next.delete(canonical);
      return next;
    });
    onFieldChange(path, removeOpeningTimesDayOverride(data, canonical, language));
  }

  function renderWeekdayGroupMenu() {
    if (openingTimesFrozen || customizableWeekdays.length === 0) {
      return null;
    }

    return (
      <details className={styles.openingTimesCaretMenu}>
        <summary
          className={styles.openingTimesCaretSummary}
          aria-label={t('openingTimes.customizeDaysMenu')}
        />
        <ul className={styles.openingTimesCaretMenuList}>
          {customizableWeekdays.map((day) => (
            <li key={day}>
              <button
                type="button"
                className={styles.openingTimesCaretMenuItem}
                onClick={() => addDayOverride(day)}
              >
                {localizeWeekdayLabel(day, t)}
              </button>
            </li>
          ))}
        </ul>
      </details>
    );
  }

  function renderGroupScheduleField(
    groupKey: OpeningTimesScheduleGroupKey,
    groupDays: readonly string[],
    groupSchedule: unknown,
    sectionLabel: string,
  ) {
    const id = `${idPrefix}-opening-group-${groupKey}`;
    const fieldPath = [...path, `__group_${groupKey}`];
    const rowReadOnly = readonlyPathPredicate?.(fieldPath) ?? false;
    const value = isOpeningTimeDayValue(groupSchedule) ? groupSchedule : 'closed';
    const showWeekdayExpand = groupKey === 'weekdays' && customizableWeekdays.length > 0;

    return (
      <div className={styles.openingTimesGroupBlock}>
        <div className={styles.openingTimesGroupRow}>
          {groupKey === 'weekdays' ? renderWeekdayGroupMenu() : null}
          <OpeningTimeDayField
            id={id}
            dayKey={groupKey}
            displayLabel={sectionLabel}
            value={value}
            fieldPath={fieldPath}
            readOnly={rowReadOnly}
            onFieldChange={(_fieldPath, nextValue) => setGroupSchedule(groupDays, nextValue)}
          />
        </div>
        {showWeekdayExpand ? (
          <button
            type="button"
            className={styles.openingTimesExpandWeekdaysButton}
            disabled={openingTimesFrozen}
            onClick={expandAllWeekdays}
          >
            {t('openingTimes.expandAllWeekdays')}
          </button>
        ) : null}
      </div>
    );
  }

  function renderOverrideRow(canonical: string) {
    const storedKey = resolveOpeningTimesStoredWeekdayKey(data, canonical);
    const value = openingTimesGetDayValue(data, canonical);
    const fieldPath = [...path, storedKey];
    const id = `${idPrefix}-${fieldPath.join('__')}`;
    const rowReadOnly = readonlyPathPredicate?.(fieldPath) ?? false;

    return (
      <div key={canonical} className={styles.openingTimesWeekdayRow}>
        {!isOpeningTimeDayValue(value) ? (
          <div className={styles.fieldRow}>
            <span className={styles.inputLabel}>{openingTimesDayDisplayLabel(storedKey, language, t)}</span>
            <pre className={styles.fallbackValue}>{toPrettyJson(value)}</pre>
          </div>
        ) : (
          <OpeningTimeDayField
            id={id}
            dayKey={storedKey}
            value={value}
            fieldPath={fieldPath}
            readOnly={rowReadOnly}
            onFieldChange={(_fieldPath, nextValue) => {
              if (openingTimesFrozen) {
                return;
              }
              onFieldChange(path, applyOpeningTimesDayOverride(data, canonical, nextValue));
            }}
          />
        )}
        {!openingTimesFrozen ? (
          <button
            type="button"
            className={styles.openingTimesRemoveSpecialButton}
            disabled={rowReadOnly}
            onClick={() => removeDayOverride(canonical)}
          >
            {t('openingTimes.removeDayOverride')}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.fieldsetLegend}>{t('assetField.openingTimes')}</legend>
      {scheduleGroups.map((group) => (
        <div key={group.key} className={styles.openingTimesSection}>
          {renderGroupScheduleField(
            group.key,
            group.days,
            groupSchedules.get(group.key) ?? 'closed',
            t(openingTimesScheduleGroupLabelKey(group.key) as MessageKey),
          )}
        </div>
      ))}
      {effectiveOverrides.length > 0 ? (
        <div className={styles.openingTimesSection}>
          <h3 className={styles.openingTimesSectionTitle}>{t('openingTimes.dayOverrides')}</h3>
          {effectiveOverrides.map(renderOverrideRow)}
        </div>
      ) : null}
      {allowSpecificDates ? (
      <>
      <details className={styles.openingTimesDatesDropdown}>
        <summary className={styles.openingTimesDatesDropdownSummary}>
          {t('openingTimes.specificDates')}
          {specificDateKeys.length > 0 ? ` (${specificDateKeys.length})` : ''}
        </summary>
        <div className={styles.openingTimesDatesDropdownBody}>
          <p className={styles.openingTimesSpecialDatesHint}>{t('openingTimes.specificDatesHint')}</p>
          <div className={styles.openingTimesAddDateRow}>
            <OpeningTimesIsoDateTripleSelect
              idPrefix={`${idPrefix}-opening-specific`}
              value={specificPickedIso}
              onChange={(iso) => {
                setSpecificPickedIso(iso);
                setSpecificDateError(null);
              }}
            />
            <button
              type="button"
              className={styles.toggleHoursButton}
              disabled={openingTimesFrozen}
              onClick={() => {
                if (!specificPickedIso.trim() || !parseIsoDateParts(specificPickedIso)) {
                  setSpecificDateError(t('openingTimes.pickDateFirst'));
                  return;
                }
                addSpecificDate(specificPickedIso);
                setSpecificPickedIso('');
              }}
            >
              {t('openingTimes.addSpecificDate')}
            </button>
          </div>
          {specificDateError ? (
            <p className={styles.picklistError} role="alert">
              {specificDateError}
            </p>
          ) : null}
          {specificDateKeys.length === 0 ? (
            <p className={styles.openingTimesNoSpecialDates}>{t('openingTimes.noSpecialDatesYet')}</p>
          ) : (
            <div className={styles.openingTimesSpecificSelectRow}>
              <label className={styles.birthDatePartLabel} htmlFor={`${idPrefix}-specific-select`}>
                {t('openingTimes.selectSpecificDate')}
              </label>
              <select
                id={`${idPrefix}-specific-select`}
                className={styles.selectInput}
                value={specificSelectedKey}
                disabled={openingTimesFrozen}
                onChange={(event) => setSpecificSelectedKey(event.target.value)}
              >
                <option value="">{t('openingTimes.selectSpecificDate')}</option>
                {specificDateKeys.map((specialKey) => (
                  <option key={specialKey} value={specialKey}>
                    {openingTimesDayDisplayLabel(specialKey, language, t)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {specificSelectedKey && specificDateKeys.includes(specificSelectedKey) ? (
            <div className={styles.openingTimesSpecialDateRow}>
              {isOpeningTimeDayValue(data[specificSelectedKey]) ? (
                <OpeningTimeDayField
                  id={`${idPrefix}-${[...path, specificSelectedKey].join('__')}`}
                  dayKey={specificSelectedKey}
                  value={data[specificSelectedKey]}
                  fieldPath={[...path, specificSelectedKey]}
                  onFieldChange={onFieldChange}
                  readOnly={readonlyPathPredicate?.([...path, specificSelectedKey]) ?? false}
                />
              ) : (
                <div className={styles.fieldRow}>
                  <span className={styles.inputLabel}>
                    {openingTimesDayDisplayLabel(specificSelectedKey, language, t)}
                  </span>
                  <pre className={styles.fallbackValue}>{toPrettyJson(data[specificSelectedKey])}</pre>
                </div>
              )}
              <button
                type="button"
                className={styles.openingTimesRemoveSpecialButton}
                disabled={openingTimesFrozen}
                onClick={() => {
                  removeSpecialDateKey(specificSelectedKey);
                  setSpecificSelectedKey('');
                }}
              >
                {t('openingTimes.remove')}
              </button>
            </div>
          ) : null}
        </div>
      </details>

      <details className={styles.openingTimesDatesDropdown}>
        <summary className={styles.openingTimesDatesDropdownSummary}>
          {t('openingTimes.blackoutDates')}
          {blackoutDateKeys.length > 0 ? ` (${blackoutDateKeys.length})` : ''}
        </summary>
        <div className={styles.openingTimesDatesDropdownBody}>
          <p className={styles.openingTimesSpecialDatesHint}>{t('openingTimes.blackoutDatesHint')}</p>
          <div className={styles.openingTimesBlackoutFields}>
            <div className={styles.openingTimesBlackoutField}>
              <span className={styles.birthDatePartLabel}>{t('openingTimes.blackoutStart')}</span>
              <OpeningTimesIsoDateTripleSelect
                idPrefix={`${idPrefix}-opening-blackout-start`}
                value={blackoutStartIso}
                onChange={syncBlackoutFromStart}
              />
            </div>
            <div className={styles.openingTimesBlackoutField}>
              <label className={styles.birthDatePartLabel} htmlFor={`${idPrefix}-blackout-days`}>
                {t('openingTimes.blackoutDays')}
              </label>
              <input
                id={`${idPrefix}-blackout-days`}
                className={styles.textInput}
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={blackoutDaysText}
                disabled={openingTimesFrozen}
                onChange={(event) => syncBlackoutFromDays(event.target.value)}
              />
            </div>
            <div className={styles.openingTimesBlackoutField}>
              <span className={styles.birthDatePartLabel}>{t('openingTimes.blackoutEnd')}</span>
              <OpeningTimesIsoDateTripleSelect
                idPrefix={`${idPrefix}-opening-blackout-end`}
                value={blackoutEndIso}
                onChange={syncBlackoutFromEnd}
              />
            </div>
          </div>
          <div className={styles.openingTimesAddDateRow}>
            <button
              type="button"
              className={styles.toggleHoursButton}
              disabled={openingTimesFrozen}
              onClick={addBlackoutDates}
            >
              {t('openingTimes.addBlackoutDates')}
            </button>
          </div>
          {blackoutError ? (
            <p className={styles.picklistError} role="alert">
              {blackoutError}
            </p>
          ) : null}
          {blackoutRanges.length > 0 ? (
            <div className={styles.blackoutRangeList}>
              <p className={styles.openingTimesSpecialDatesHint}>{t('openingTimes.blackoutRangesListHint')}</p>
              {blackoutRanges.map((range) => (
                <BlackoutRangeRow
                  key={`${range.startIso}__${range.endIso}`}
                  range={range}
                  idPrefix={idPrefix}
                  readOnly={openingTimesFrozen}
                  language={language}
                  onSave={saveBlackoutRange}
                  onRemove={removeBlackoutRange}
                />
              ))}
            </div>
          ) : (
            <p className={styles.openingTimesSpecialDatesHint}>{t('openingTimes.noBlackoutDatesYet')}</p>
          )}
        </div>
      </details>
      </>
      ) : null}
    </fieldset>
  );
}

function parseNumberList(input: string): number[] {
  if (!input.trim()) {
    return [];
  }
  return input
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => !Number.isNaN(n));
}

type JsonDraftProps = {
  id: string;
  label: string;
  value: unknown;
  onCommit: (next: unknown) => void;
  rows: number;
  readOnly?: boolean;
};

/**
 * Object/mixed JSON arrays: edit as text, commit on blur (so partial JSON is possible while typing).
 */
type StringListDraftProps = {
  id: string;
  fieldKey: string;
  fieldPath: string[];
  value: string[];
  onFieldChange: (path: string[], value: unknown) => void;
  readOnly?: boolean;
};

function StringListDraftField({ id, fieldKey, fieldPath, value, onFieldChange, readOnly }: StringListDraftProps) {
  const { t } = useI18n();
  const [text, setText] = useState(() => value.join('\n'));
  const sig = useMemo(() => value.join('\n'), [value]);

  useEffect(() => {
    setText(value.join('\n'));
  }, [sig, value]);

  return (
    <div className={styles.fieldRow}>
      <label className={styles.inputLabel} htmlFor={id}>
        {localizeAssetFieldLabel(fieldKey, t)}
      </label>
      <p className={styles.arrayHint} id={`${id}-hint`}>
        One value per line (e.g. image URLs)
      </p>
      <textarea
        id={id}
        className={`${styles.jsonTextarea} ${styles.stringListTextarea}`}
        value={text}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={(e) => {
          if (!readOnly) {
            setText(e.target.value);
          }
        }}
        onBlur={() => {
          if (readOnly) {
            return;
          }
          const lines = text
            .split('\n')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
          onFieldChange(fieldPath, lines);
        }}
        aria-describedby={`${id}-hint`}
      />
    </div>
  );
}

function EditableJsonDraftField({ id, label, value, onCommit, rows, readOnly }: JsonDraftProps) {
  const [text, setText] = useState(() => toPrettyJson(value));
  const valueSig = useMemo(() => {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }, [value]);

  useEffect(() => {
    setText(toPrettyJson(value));
  }, [valueSig, value]);

  return (
    <div className={styles.fieldRow}>
      <label className={styles.inputLabel} htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className={styles.jsonTextarea}
        value={text}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={(e) => {
          if (!readOnly) {
            setText(e.target.value);
          }
        }}
        onBlur={() => {
          if (readOnly) {
            return;
          }
          try {
            onCommit(JSON.parse(text) as unknown);
          } catch {
            setText(toPrettyJson(value));
          }
        }}
        rows={rows}
        spellCheck={false}
      />
    </div>
  );
}

function FormFields({
  data,
  path,
  onFieldChange,
  idPrefix,
  showPasswordRequirements = false,
  generalInfoPicklists,
  mandatoryMarkerKeys,
  rootFieldLabelKeys,
  birthDateDayMonthYear = false,
  registerGenderMaleFemaleOther = false,
  readonlyPathPredicate,
  theme = 'dark',
  compactLayout = false,
  openingTimesAllowSpecificDates = true,
}: PayloadFormFieldsProps) {
  const { language, t } = useI18n();
  const labelForKey = (key: string) => {
    if (path.length === 0 && rootFieldLabelKeys?.[key]) {
      return t(rootFieldLabelKeys[key]!);
    }
    return localizeAssetFieldLabel(key, t);
  };
  const keys = useMemo(() => sortRecordKeysForDisplay(path, Object.keys(data)), [data, path]);
  return (
    <div className={fieldsContainerClassName(compactLayout, path)}>
      {keys.map((key) => {
        const value = data[key];
        const fieldPath = [...path, key];
        const id = `${idPrefix}-${fieldPath.join('__')}`;
        const mandatory = showMandatoryMarker(path, key, mandatoryMarkerKeys);
        const ro = readonlyPathPredicate?.(fieldPath) ?? false;

        if (isOpeningTimesDayPath(fieldPath) && isOpeningTimeDayValue(value)) {
          return (
            <OpeningTimeDayField
              key={key}
              id={id}
              dayKey={key}
              value={value}
              fieldPath={fieldPath}
              onFieldChange={onFieldChange}
              readOnly={ro}
            />
          );
        }

        if (isPlainObject(value)) {
          if (key === 'opening_times') {
            return (
              <OpeningTimesFieldset
                key={key}
                data={value}
                path={fieldPath}
                idPrefix={idPrefix}
                onFieldChange={onFieldChange}
                readonlyPathPredicate={readonlyPathPredicate}
                allowSpecificDates={openingTimesAllowSpecificDates}
              />
            );
          }
          const addressFieldset = normalizeWireKey(key) === 'address';
          if (addressFieldset && isAddressFieldGroup(Object.keys(value))) {
            return (
              <fieldset key={key} className={fieldsetClassName(compactLayout, key)}>
                <legend
                  className={`${styles.fieldsetLegend} ${styles.fieldsetLegendAddress}`}
                >
                  {labelForKey(key)}
                </legend>
                <IsraelAddressFieldset
                  data={value}
                  path={fieldPath}
                  idPrefix={idPrefix}
                  onFieldChange={onFieldChange}
                  generalInfoPicklists={generalInfoPicklists}
                  mandatoryMarkerKeys={mandatoryMarkerKeys}
                  readonlyPathPredicate={readonlyPathPredicate}
                  theme={theme}
                  compactLayout={compactLayout}
                />
              </fieldset>
            );
          }
          return (
            <fieldset key={key} className={fieldsetClassName(compactLayout, key)}>
              <legend
                className={
                  addressFieldset
                    ? `${styles.fieldsetLegend} ${styles.fieldsetLegendAddress}`
                    : styles.fieldsetLegend
                }
              >
                {labelForKey(key)}
              </legend>
              <FormFields
                data={value}
                path={fieldPath}
                onFieldChange={onFieldChange}
                idPrefix={idPrefix}
                showPasswordRequirements={showPasswordRequirements}
                generalInfoPicklists={undefined}
                mandatoryMarkerKeys={mandatoryMarkerKeys}
                rootFieldLabelKeys={rootFieldLabelKeys}
                birthDateDayMonthYear={false}
                registerGenderMaleFemaleOther={false}
                readonlyPathPredicate={readonlyPathPredicate}
                theme={theme}
                compactLayout={compactLayout}
              />
            </fieldset>
          );
        }

        if (Array.isArray(value)) {
          if (isNumberArray(value)) {
            const listHint =
              key === 'facilities'
                ? 'Enter facility IDs as a comma- or semicolon-separated list (e.g. 46, 47, 48)'
                : 'Comma-separated numbers (e.g. 46, 47, 48)';
            return (
              <div key={key} className={styles.fieldRow}>
                <label className={styles.inputLabel} htmlFor={id}>
                  {labelForKey(key)}
                </label>
                <p className={styles.arrayHint} id={`${id}-hint`}>
                  {listHint}
                </p>
                <input
                  id={id}
                  className={styles.textInput}
                  type="text"
                  readOnly={ro}
                  disabled={ro}
                  value={value.join(', ')}
                  onChange={(event) => onFieldChange(fieldPath, parseNumberList(event.target.value))}
                  aria-describedby={`${id}-hint`}
                  title={
                    ro
                      ? 'Not sent with PATCH — this field cannot be updated from this endpoint'
                      : undefined
                  }
                />
              </div>
            );
          }
          if (isStringArray(value)) {
            return (
              <StringListDraftField
                key={key}
                id={id}
                fieldKey={key}
                fieldPath={fieldPath}
                onFieldChange={onFieldChange}
                value={value}
                readOnly={ro}
              />
            );
          }
          return (
            <EditableJsonDraftField
              key={key}
              id={id}
              label={`${labelForKey(key)} (JSON array)`}
              value={value}
              onCommit={(next) => onFieldChange(fieldPath, next)}
              rows={5}
              readOnly={ro}
            />
          );
        }

        if (typeof value === 'boolean') {
          return (
            <div key={key} className={styles.checkboxRow}>
              <input
                id={id}
                className={styles.checkbox}
                type="checkbox"
                checked={value}
                readOnly={ro}
                disabled={ro}
                onChange={(event) => onFieldChange(fieldPath, event.target.checked)}
              />
              <label htmlFor={id} className={styles.inputLabel}>
                {labelForKey(key)}
              </label>
            </div>
          );
        }

        const picklists = generalInfoPicklists && path.length === 0 ? generalInfoPicklists : undefined;
        const canCoerceNumericPicklistRootString =
          picklists !== undefined &&
          typeof value === 'string' &&
          (key === 'country_id' || key === 'state_id' || key === 'asset_type_id') &&
          coerceRootNumericId(value) !== null;

        if (typeof value === 'number' || value === null || canCoerceNumericPicklistRootString) {
          const idForPicklistUi = coerceRootNumericId(value);
          const strVal = idForPicklistUi === null ? '' : String(idForPicklistUi);

          if (picklists && key === 'country_id') {
            const st = picklists.countriesStatus;
            const blocked = st === 'loading' || st === 'error';
            const placeholderLabel =
              st === 'loading'
                ? t('form.picklist.loadingCountries')
                : st === 'error'
                  ? t('form.picklist.unavailable')
                  : t('form.picklist.selectCountry');

            return (
              <div key={key} className={fieldRowClassName(compactLayout, path, key)}>
                <label htmlFor={id} className={styles.inputLabel}>
                  {labelForKey(key)}
                  <MandatoryAsterisk visible={mandatory} />
                </label>
                {picklists.countriesError ? (
                  <p className={styles.picklistError} role="alert">
                    {picklists.countriesError}
                  </p>
                ) : null}
                <select
                  id={id}
                  className={styles.selectInput}
                  aria-invalid={st === 'error'}
                  aria-busy={st === 'loading'}
                  disabled={blocked || ro}
                  value={strVal}
                  aria-required={mandatory || undefined}
                  title={ro ? 'Not applied on PATCH for this endpoint' : undefined}
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === '') {
                      onFieldChange(fieldPath, null);
                      return;
                    }
                    const n = Number(raw);
                    onFieldChange(fieldPath, Number.isNaN(n) ? null : n);
                  }}
                >
                  <option value="">{placeholderLabel}</option>
                  {picklists.countries.map((item) => (
                    <option key={item.id} value={String(item.id)}>
                      {localizeGeoLabel(item.label, language)}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          if (picklists && key === 'state_id') {
            const countryRaw = data.country_id;
            const countryNum = coerceRootNumericId(countryRaw);
            const hasCountry = countryNum !== null;

            const st = picklists.regionsStatus;
            const noRegionsFromApi =
              hasCountry && st === 'ready' && picklists.regions.length === 0;

            const currentIdNum =
              strVal !== '' && !Number.isNaN(Number(strVal)) ? Number(strVal) : null;

            /** Require country; keep enabled during loading/errors so any loaded district row stays selectable. */
            const regionSelectDisabled = !hasCountry;

            let placeholderLabel = t('form.picklist.selectRegion');
            if (!hasCountry) {
              placeholderLabel = t('assets.filter.selectCountryFirst');
            } else if (st === 'loading') {
              placeholderLabel = t('form.picklist.loadingRegions');
            } else if (st === 'error') {
              placeholderLabel =
                picklists.regions.length > 0
                  ? t('form.picklist.pickRegionStale')
                  : t('form.picklist.regionsUnavailable');
            } else if (noRegionsFromApi) {
              placeholderLabel = t('form.picklist.noRegionsApi');
            }

            let options = picklists.regions;
            if (
              currentIdNum != null &&
              !picklists.regions.some((r) => Number(r.id) === currentIdNum) &&
              (st === 'ready' || st === 'loading' || st === 'error')
            ) {
              options = [{ id: currentIdNum, label: `#${currentIdNum} (current)` }, ...picklists.regions];
            }

            const selectValue =
              !hasCountry ||
              (st === 'ready' &&
                picklists.regions.length === 0 &&
                currentIdNum == null)
                ? ''
                : strVal;

            return (
              <div key={key} className={fieldRowClassName(compactLayout, path, key)}>
                <label htmlFor={id} className={styles.inputLabel}>
                  {labelForKey(key)}
                  <MandatoryAsterisk visible={mandatory} />
                </label>
                {picklists.regionsError ? (
                  <p className={styles.picklistError} role="alert">
                    {picklists.regionsError}
                  </p>
                ) : null}
                <select
                  key={`region-country-${countryNum ?? 'none'}`}
                  id={id}
                  className={styles.selectInput}
                  aria-invalid={st === 'error'}
                  aria-busy={st === 'loading'}
                  disabled={regionSelectDisabled || ro}
                  value={selectValue}
                  aria-required={mandatory || undefined}
                  title={ro ? 'Not applied on PATCH for this endpoint' : undefined}
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === '') {
                      onFieldChange(fieldPath, null);
                      return;
                    }
                    const n = Number(raw);
                    onFieldChange(fieldPath, Number.isNaN(n) ? null : n);
                  }}
                >
                  <option value="">{placeholderLabel}</option>
                  {options.map((item) => (
                    <option key={`${item.id}-${item.label}`} value={String(item.id)}>
                      {localizeGeoLabel(item.label, language)}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          if (
            picklists &&
            key === 'asset_type_id' &&
            picklists.assetTypesStatus !== undefined
          ) {
            const st = picklists.assetTypesStatus;
            const blocked = st === 'loading' || st === 'error';
            const placeholderLabel =
              st === 'loading'
                ? t('form.picklist.loadingAssetTypes')
                : st === 'error'
                  ? t('form.picklist.unavailable')
                  : t('form.picklist.selectAssetType');

            let options = picklists.assetTypes ?? [];
            const currentTypeId =
              strVal !== '' && !Number.isNaN(Number(strVal)) ? Number(strVal) : null;
            if (
              currentTypeId != null &&
              !options.some((r) => r.id === currentTypeId) &&
              st === 'ready'
            ) {
              options = [{ id: currentTypeId, label: `#${currentTypeId} (current)` }, ...options];
            }

            return (
              <div key={key} className={fieldRowClassName(compactLayout, path, key)}>
                <label htmlFor={id} className={styles.inputLabel}>
                  {labelForKey(key)}
                  <MandatoryAsterisk visible={mandatory} />
                </label>
                {picklists.assetTypesError ? (
                  <p className={styles.picklistError} role="alert">
                    {picklists.assetTypesError}
                  </p>
                ) : null}
                <select
                  id={id}
                  className={styles.selectInput}
                  aria-invalid={st === 'error'}
                  aria-busy={st === 'loading'}
                  disabled={blocked || ro}
                  value={strVal}
                  aria-required={mandatory || undefined}
                  title={ro ? 'Not applied on PATCH for this endpoint' : undefined}
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === '') {
                      onFieldChange(fieldPath, null);
                      return;
                    }
                    const n = Number(raw);
                    onFieldChange(fieldPath, Number.isNaN(n) ? null : n);
                  }}
                >
                  <option value="">{placeholderLabel}</option>
                  {options.map((item) => (
                    <option key={item.id} value={String(item.id)}>
                      {localizeAssetTypeLabel(item.label, language, t)}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          return (
            <div key={key} className={fieldRowClassName(compactLayout, path, key)}>
              <label htmlFor={id} className={styles.inputLabel}>
                {labelForKey(key)}
                <MandatoryAsterisk visible={mandatory} />
              </label>
              <input
                id={id}
                className={styles.textInput}
                type="number"
                aria-required={mandatory || undefined}
                readOnly={ro}
                disabled={ro}
                value={
                  value === null || value === undefined || Number.isNaN(value as number) ? '' : value
                }
                title={ro ? 'Not sent with PATCH — field omitted or ignored by this endpoint' : undefined}
                onChange={(event) => {
                  const raw = event.target.value;
                  if (raw === '') {
                    onFieldChange(fieldPath, null);
                    return;
                  }
                  const n = Number(raw);
                  onFieldChange(fieldPath, Number.isNaN(n) ? null : n);
                }}
              />
            </div>
          );
        }

        if (typeof value === 'string') {
          const str = value;
          const lower = key.toLowerCase();

          if (isAddressCityField(path, key)) {
            return (
              <IsraelSettlementCitySelect
                key={key}
                id={id}
                label={labelForKey(key)}
                value={str}
                onChange={(next) => onFieldChange(fieldPath, next)}
                disabled={ro}
                readOnly={ro}
                mandatory={mandatory}
                theme={theme}
                rowClassName={fieldRowClassName(compactLayout, path, key)}
              />
            );
          }

          const isPassword = lower === 'password' || lower.endsWith('_password');
          const isEmail = lower === 'email' || lower.endsWith('_email');
          const isPhone = isPhoneWireFieldKey(key);
          const isDate =
            lower === 'birth_date' || (lower.endsWith('_date') && !lower.includes('registration'));

          if (birthDateDayMonthYear && path.length === 0 && key === 'birth_date') {
            return (
              <BirthDateSplitPicker
                key={key}
                id={id}
                value={str}
                onChange={(next) => onFieldChange(fieldPath, next)}
                mandatory={mandatory}
                theme={theme}
              />
            );
          }

          if (registerGenderMaleFemaleOther && path.length === 0 && key === 'gender') {
            const selectVal = canonicalGenderSelectValue(str);
            return (
              <div key={key} className={styles.fieldRow}>
                <label htmlFor={id} className={styles.inputLabel}>
                  {labelForKey(key)}
                  <MandatoryAsterisk visible={mandatory} />
                </label>
                <select
                  id={id}
                  className={styles.selectInput}
                  value={selectVal}
                  aria-required={mandatory || undefined}
                  onChange={(event) => onFieldChange(fieldPath, event.target.value)}
                >
                  <option value="">{t('profile.gender.select')}</option>
                  <option value="male">{t('profile.gender.male')}</option>
                  <option value="female">{t('profile.gender.female')}</option>
                  <option value="other">{t('profile.gender.other')}</option>
                </select>
              </div>
            );
          }
          if (isPassword) {
            const policySuffix =
              showPasswordRequirements && lower === 'password'
                ? ` ${t('auth.passwordRequirementsInParentheses')}`
                : '';
            return (
              <div key={key} className={styles.fieldRow}>
                <label htmlFor={id} className={styles.inputLabel}>
                  {labelForKey(key)}
                  <MandatoryAsterisk visible={mandatory} />
                  {policySuffix ? <>{policySuffix}</> : null}
                </label>
                <PasswordInputRow
                  id={id}
                  value={str}
                  onChange={(next) => onFieldChange(fieldPath, next)}
                  inputClassName={formInputClassName(
                    styles.textInput,
                    key,
                    directionStyles.inputLtr,
                    directionStyles.inputNameField,
                  )}
                  toggleClassName={theme === 'light' ? styles.passwordToggle : undefined}
                  autoComplete="new-password"
                  ariaRequired={mandatory}
                  forceLtr
                />
              </div>
            );
          }
          const inputType = isEmail
            ? 'email'
            : isPhone
              ? 'tel'
              : isDate && /^\d{4}-\d{2}-\d{2}$/.test(str)
                ? 'date'
                : 'text';
          if (path.length === 0 && key === 'description') {
            return (
              <div key={key} className={fieldRowClassName(compactLayout, path, key)}>
                <label htmlFor={id} className={styles.inputLabel}>
                  {labelForKey(key)}
                  <MandatoryAsterisk visible={mandatory} />
                </label>
                <textarea
                  id={id}
                  className={styles.descriptionTextarea}
                  rows={2}
                  value={str}
                  readOnly={ro}
                  disabled={ro}
                  title={ro ? 'Not sent with PATCH — field omitted or ignored by this endpoint' : undefined}
                  onChange={(event) => onFieldChange(fieldPath, event.target.value)}
                  aria-required={mandatory || undefined}
                />
              </div>
            );
          }
          return (
            <div key={key} className={fieldRowClassName(compactLayout, path, key)}>
              <label htmlFor={id} className={styles.inputLabel}>
                {labelForKey(key)}
                <MandatoryAsterisk visible={mandatory} />
              </label>
              <input
                id={id}
                className={formInputClassName(
                  styles.textInput,
                  key,
                  directionStyles.inputLtr,
                  directionStyles.inputNameField,
                )}
                type={inputType}
                dir={formInputDir(key, language)}
                inputMode={isPhone ? 'tel' : isEmail ? 'email' : undefined}
                autoCapitalize={isEmail ? 'none' : undefined}
                autoCorrect={isEmail ? 'off' : undefined}
                spellCheck={isEmail ? false : undefined}
                value={str}
                readOnly={ro}
                disabled={ro}
                title={ro ? 'Not sent with PATCH — field omitted or ignored by this endpoint' : undefined}
                onChange={(event) => onFieldChange(fieldPath, event.target.value)}
                aria-required={mandatory || undefined}
              />
            </div>
          );
        }

        return (
          <div key={key} className={fieldRowClassName(compactLayout, path, key)}>
            <span className={styles.inputLabel}>{labelForKey(key)}</span>
            <pre
              className={styles.fallbackValue}
              title={ro ? 'Not sent with PATCH — omit or nested display-only payload' : undefined}
            >
              {toPrettyJson(value)}
            </pre>
          </div>
        );
      })}
    </div>
  );
}

export type DynamicRecordFormProps = {
  data: Record<string, unknown>;
  onFieldChange: (path: string[], value: unknown) => void;
  idPrefix: string;
  /**
   * Change this (e.g. increment) after resetting `data` to the same shape so draft controls
   * (JSON textareas, uncommitted string lists) remount. Parent `key=…` is not always enough
   * because the wrapper may be what gets keyed, not the inner draft state.
   */
  remountKey?: string | number;
  showPasswordRequirements?: boolean;
  generalInfoPicklists?: GeneralInfoPicklistsProp;
  mandatoryMarkerKeys?: readonly string[];
  rootFieldLabelKeys?: Partial<Record<string, MessageKey>>;
  birthDateDayMonthYear?: boolean;
  registerGenderMaleFemaleOther?: boolean;
  /** When set, paths that match cannot be edited (e.g. UpdateAsset PATCH forbid / ignored roots). */
  readonlyPathPredicate?: (pathSegments: readonly string[]) => boolean;
  theme?: 'dark' | 'light';
  compactLayout?: boolean;
  /** When false, hide specific-date and blackout date overrides. */
  openingTimesAllowSpecificDates?: boolean;
};

export function DynamicRecordForm({
  data,
  onFieldChange,
  idPrefix,
  remountKey = 0,
  showPasswordRequirements = false,
  generalInfoPicklists,
  mandatoryMarkerKeys,
  rootFieldLabelKeys,
  birthDateDayMonthYear = false,
  registerGenderMaleFemaleOther = false,
  readonlyPathPredicate,
  theme = 'dark',
  compactLayout = false,
  openingTimesAllowSpecificDates = true,
}: DynamicRecordFormProps) {
  const fields = (
    <FormFields
      key={String(remountKey)}
      data={data}
      path={[]}
      onFieldChange={onFieldChange}
      idPrefix={idPrefix}
      showPasswordRequirements={showPasswordRequirements}
      generalInfoPicklists={generalInfoPicklists}
      mandatoryMarkerKeys={mandatoryMarkerKeys}
      rootFieldLabelKeys={rootFieldLabelKeys}
      birthDateDayMonthYear={birthDateDayMonthYear}
      registerGenderMaleFemaleOther={registerGenderMaleFemaleOther}
      readonlyPathPredicate={readonlyPathPredicate}
      theme={theme}
      compactLayout={compactLayout}
      openingTimesAllowSpecificDates={openingTimesAllowSpecificDates}
    />
  );

  if (theme === 'light') {
    return <div className={styles.lightTheme}>{fields}</div>;
  }

  return fields;
}
